/**
 * CMS shell: sidebar, top bar and content region.
 *
 * Sidebar entries are filtered by the signed-in user's capabilities, so an Author never sees links
 * to settings or user management. That is a usability measure — the real enforcement is in the API
 * and in the route guards.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui';
import { Icon } from '@/components/site/blocks';
import { useAuth } from '@/context/AuthContext';
import { useScrollLock } from '@/hooks';
import { cn } from '@/lib/cn';
import type { Capabilities } from '@/services/types';

interface NavEntry {
  label: string;
  path: string;
  icon: string;
  capability?: keyof Capabilities;
  end?: boolean;
}

interface NavGroup {
  title: string;
  entries: NavEntry[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Overview',
    entries: [{ label: 'Dashboard', path: '/admin', icon: 'grid', end: true }],
  },
  {
    title: 'Content',
    entries: [
      { label: 'Pages', path: '/admin/pages', icon: 'file-text' },
      { label: 'Products', path: '/admin/products', icon: 'cpu' },
      { label: 'Solutions', path: '/admin/solutions', icon: 'layers' },
      { label: 'Services', path: '/admin/services', icon: 'settings' },
      { label: 'Industries', path: '/admin/industries', icon: 'building' },
      { label: 'Case studies', path: '/admin/case-studies', icon: 'award' },
      { label: 'Articles', path: '/admin/articles', icon: 'pen' },
    ],
  },
  {
    title: 'Engagement',
    entries: [
      { label: 'Leads', path: '/admin/leads', icon: 'users', capability: 'can_manage_leads' },
      { label: 'Applications', path: '/admin/careers/applications', icon: 'file-text', capability: 'can_manage_applications' },
    ],
  },
  {
    title: 'Library',
    entries: [{ label: 'Media', path: '/admin/media', icon: 'archive' }],
  },
  {
    title: 'Administration',
    entries: [
      { label: 'Settings', path: '/admin/settings', icon: 'settings', capability: 'can_manage_settings' },
      { label: 'Users & roles', path: '/admin/users', icon: 'key', capability: 'can_manage_users' },
      { label: 'Audit log', path: '/admin/audit', icon: 'activity', capability: 'can_view_audit' },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth();

  return (
    <nav aria-label="CMS navigation" className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-5">
      {NAV_GROUPS.map((group) => {
        const entries = group.entries.filter((entry) => !entry.capability || can(entry.capability));
        if (entries.length === 0) return null;
        return (
          <div key={group.title}>
            <p className="px-3 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-muted">{group.title}</p>
            <ul className="mt-2 space-y-0.5">
              {entries.map((entry) => (
                <li key={entry.path}>
                  <NavLink
                    to={entry.path}
                    end={entry.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm no-underline transition-colors hover:no-underline',
                        isActive ? 'bg-primary-soft font-semibold text-primary' : 'text-ink-soft hover:bg-raised',
                      )
                    }
                  >
                    <Icon name={entry.icon} className="h-4 w-4 shrink-0" />
                    {entry.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-raised focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-contrast">
          {user.initials}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block text-sm font-medium leading-tight text-ink">{user.full_name}</span>
          <span className="block text-xs capitalize leading-tight text-muted">{user.primary_role.replace('_', ' ')}</span>
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[60] mt-1 w-56 overflow-hidden rounded-md border border-line bg-surface shadow-float"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-sm font-medium text-ink">{user.email}</p>
            <p className="mt-0.5 text-xs text-muted">Roles: {user.roles.join(', ').replace(/_/g, ' ')}</p>
          </div>
          <Link
            to="/admin/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-ink no-underline hover:bg-raised hover:no-underline"
          >
            Your profile
          </Link>
          <Link
            to="/"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-ink no-underline hover:bg-raised hover:no-underline"
          >
            View the website
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              await signOut();
              navigate('/admin/login');
            }}
            className="w-full border-t border-line px-4 py-2.5 text-left text-sm text-danger hover:bg-danger-soft"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useScrollLock(mobileNavOpen);
  useEffect(() => setMobileNavOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen bg-raised">
      <header className="sticky top-0 z-50 border-b border-line bg-surface">
        <div className="flex h-14 items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open CMS navigation"
              className="rounded-md p-2 text-muted hover:bg-raised lg:hidden"
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5">
                <path d="M3 6h14M3 10h14M3 14h14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
            <Link to="/admin" className="flex items-center gap-2 no-underline hover:no-underline">
              <span className="flex h-7 w-7 items-center justify-center rounded bg-primary text-xs font-bold text-primary-contrast">
                S
              </span>
              <span className="font-display text-sm font-bold text-ink">Shyena CMS</span>
            </Link>
          </div>
          <UserMenu />
        </div>
      </header>

      {user?.must_change_password && (
        <div className="px-4 pt-4">
          <Alert tone="warning" title="Change your password">
            You are signed in with a temporary password.{' '}
            <Link to="/admin/profile" className="font-semibold">
              Set a new one now
            </Link>
            .
          </Alert>
        </div>
      )}

      <div className="flex">
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-64 shrink-0 border-r border-line bg-surface lg:block">
          <Sidebar />
        </aside>

        {mobileNavOpen && (
          <div className="fixed inset-0 z-[70] lg:hidden">
            <button type="button" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} className="absolute inset-0 bg-ink/50" />
            <div className="absolute inset-y-0 left-0 w-72 animate-slide-in-right bg-surface shadow-float">
              <div className="flex h-14 items-center justify-between border-b border-line px-4">
                <span className="font-display text-sm font-bold">Shyena CMS</span>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation"
                  className="rounded p-2 text-muted hover:bg-raised"
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </div>
              <Sidebar onNavigate={() => setMobileNavOpen(false)} />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
